// src/pages/AddProperty.jsx
import React, { useState, useEffect } from 'react';
import { useMutation, useQuery } from '@apollo/client';
import { Link, useNavigate } from 'react-router-dom';
import { suggestReferenceNumbers } from '../shared/utils/reference-number-helper';
import {GET_LANDLORD, GET_ALL_LANDLORDS} from "../graphql/landlords";
import {GET_ALL_AGENTS} from "../graphql/agents";
import {GET_TENANT, GET_ALL_TENANTS} from "../graphql/tenants";
import {CREATE_PROPERTY} from "../graphql/properties";

const AddProperty = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    street: '',
    suburb: '',
    town: '',
    province: '',
    postalCode: '',
    referenceNumber: '',
    electricityMeterNumber: '',
    landlordId: '',
    agentId: '',
    tenantId: '',
    status: 'Vacant Rental',
    bedrooms: '',
    bathrooms: '',
    garages: '',
    description: ''
  });
  
  const [errors, setErrors] = useState({});
  const [suggestedReferences, setSuggestedReferences] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  
  const { loading: loadingLandlords, data: landlordsData } = useQuery(GET_ALL_LANDLORDS);
  const { loading: loadingAgents, data: agentsData } = useQuery(GET_ALL_AGENTS);
  const { loading: loadingTenants, data: tenantsData } = useQuery(GET_ALL_TENANTS);

  // Additional queries to get detailed landlord and tenant info when selected
  const { data: landlordData } = useQuery(GET_LANDLORD, {
    variables: { id: formData.landlordId },
    skip: !formData.landlordId
  });

  const { data: tenantData } = useQuery(GET_TENANT, {
    variables: { id: formData.tenantId },
    skip: !formData.tenantId
  });
  
  const [createProperty, { loading: loadingCreate }] = useMutation(CREATE_PROPERTY, {
    onCompleted: (data) => {
      navigate(`/properties/${data.createProperty.id}`);
    },
    onError: (error) => {
      console.error('Error creating property:', error);
      // Display error message to the user
      setErrors({
        ...errors,
        submit: error.message
      });
    }
  });
  
  // Update suggestions whenever landlord, tenant or street changes
  useEffect(() => {
    if (landlordData?.landlord) {
      const landlord = landlordData.landlord;
      const tenant = tenantData?.tenant || null;
      const street = formData.street || '';
      
      const suggestions = suggestReferenceNumbers(landlord, tenant, street);
      setSuggestedReferences(suggestions);
    } else {
      setSuggestedReferences([]);
    }
  }, [landlordData, tenantData, formData.street]);
  
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
    
    // Clear error for this field when user types
    if (errors[name]) {
      setErrors({
        ...errors,
        [name]: ''
      });
    }
  };

  const handleReferenceSelect = (reference) => {
    setFormData({
      ...formData,
      referenceNumber: reference
    });
    setShowSuggestions(false);
  };
  
  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.street.trim()) {
      newErrors.street = 'Street address is required';
    }
    
    if (!formData.suburb.trim()) {
      newErrors.suburb = 'Suburb is required';
    }
    
    if (!formData.town.trim()) {
      newErrors.town = 'Town is required';
    }
    
    if (!formData.province.trim()) {
      newErrors.province = 'Province is required';
    }
    
    if (!formData.postalCode.trim()) {
      newErrors.postalCode = 'Postal code is required';
    }
    
    if (!formData.landlordId) {
      newErrors.landlordId = 'Landlord selection is required';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };
  
  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (validateForm()) {
      const mutationInput = {
        address: {
          street: formData.street,
          suburb: formData.suburb,
          city: formData.town,
          province: formData.province || 'Not specified',
          postalCode: formData.postalCode
        },
        referenceNumber: formData.referenceNumber || null,
        electricityMeterNumber: formData.electricityMeterNumber || null,
        landlord: formData.landlordId,
        agent: formData.agentId || null,
        tenant: formData.tenantId || null,
        status: formData.status,
        bedrooms: formData.bedrooms ? parseInt(formData.bedrooms) : null,
        bathrooms: formData.bathrooms ? parseInt(formData.bathrooms) : null,
        garages: formData.garages ? parseInt(formData.garages) : null,
        description: formData.description || null
      };
      console.log('Submitting mutation with input:', mutationInput);
      createProperty({
        variables: {
          input: mutationInput
        }
      });
    }
  };
  
  // Prepare landlord options
  const landlordOptions = landlordsData?.landlords?.items || [];
  const agentOptions = agentsData?.agents?.items || [];
  const tenantOptions = tenantsData?.tenants?.items || [];
  
  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-3xl font-semibold flex items-center">
          <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
          </svg>
          Add Property
        </h1>
        <Link to="/properties" className="flex items-center text-blue-600 hover:text-blue-800">
          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
          </svg>
          Back to Properties
        </Link>
      </div>
      
      {errors.submit && (
        <div className="mb-6 bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative" role="alert">
          <strong className="font-bold">Error!</strong>
          <span className="block sm:inline"> {errors.submit}</span>
        </div>
      )}
      
      <div className="bg-white shadow rounded-lg p-6">
        <form onSubmit={handleSubmit}>
          <div className="mb-6">
            <h2 className="text-lg font-medium text-gray-900 mb-4">Property Address</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Reference Number (New field) */}
              <div className="relative">
                <label htmlFor="referenceNumber" className="block text-sm font-medium text-gray-700 mb-1">
                  Reference Number
                </label>
                <div className="flex">
                  <input
                    type="text"
                    id="referenceNumber"
                    name="referenceNumber"
                    value={formData.referenceNumber}
                    onChange={handleChange}
                    className={`block w-full px-3 py-2 border ${errors.referenceNumber ? 'border-red-500' : 'border-gray-300'} rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm`}
                    placeholder="e.g. TEN001-LAND002"
                    onFocus={() => formData.landlordId && setShowSuggestions(true)}
                  />
                  {formData.landlordId && (
                    <button
                      type="button"
                      onClick={() => setShowSuggestions(!showSuggestions)}
                      className="ml-2 inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                      </svg>
                    </button>
                  )}
                </div>
                {errors.referenceNumber && (
                  <p className="mt-1 text-sm text-red-600">{errors.referenceNumber}</p>
                )}
                <p className="mt-1 text-xs text-gray-500">
                  Optional. Create a unique reference for this property.
                </p>
                
                {/* Reference suggestions dropdown */}
                {showSuggestions && suggestedReferences.length > 0 && (
                  <div className="absolute z-10 mt-1 w-full bg-white shadow-lg max-h-60 rounded-md py-1 text-base ring-1 ring-black ring-opacity-5 overflow-auto focus:outline-none sm:text-sm">
                    {suggestedReferences.map((reference, index) => (
                      <div
                        key={index}
                        className="cursor-pointer select-none relative py-2 pl-3 pr-9 hover:bg-gray-100"
                        onClick={() => handleReferenceSelect(reference)}
                      >
                        {reference}
                      </div>
                    ))}
                  </div>
                )}
              </div>
              
              {/* Electricity Meter Number */}
              <div>
                <label htmlFor="electricityMeterNumber" className="block text-sm font-medium text-gray-700 mb-1">
                  Electricity Meter Number
                </label>
                <input
                  type="text"
                  id="electricityMeterNumber"
                  name="electricityMeterNumber"
                  value={formData.electricityMeterNumber}
                  onChange={handleChange}
                  className={`block w-full px-3 py-2 border ${errors.electricityMeterNumber ? 'border-red-500' : 'border-gray-300'} rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm`}
                  placeholder="e.g. 12345678"
                />
                {errors.electricityMeterNumber && (
                  <p className="mt-1 text-sm text-red-600">{errors.electricityMeterNumber}</p>
                )}
                <p className="mt-1 text-xs text-gray-500">
                  Unique municipal electricity meter number for this property.
                </p>
              </div>
              
              {/* Street */}
              <div className="md:col-span-2">
                <label htmlFor="street" className="block text-sm font-medium text-gray-700 mb-1">
                  Street Address*
                </label>
                <input
                  type="text"
                  id="street"
                  name="street"
                  value={formData.street}
                  onChange={handleChange}
                  className={`block w-full px-3 py-2 border ${errors.street ? 'border-red-500' : 'border-gray-300'} rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm`}
                />
                {errors.street && (
                  <p className="mt-1 text-sm text-red-600">{errors.street}</p>
                )}
              </div>
              
              {/* Suburb */}
              <div>
                <label htmlFor="suburb" className="block text-sm font-medium text-gray-700 mb-1">
                  Suburb*
                </label>
                <input
                  type="text"
                  id="suburb"
                  name="suburb"
                  value={formData.suburb}
                  onChange={handleChange}
                  className={`block w-full px-3 py-2 border ${errors.suburb ? 'border-red-500' : 'border-gray-300'} rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm`}
                />
                {errors.suburb && (
                  <p className="mt-1 text-sm text-red-600">{errors.suburb}</p>
                )}
              </div>
              
              {/* Town */}
              <div>
                <label htmlFor="town" className="block text-sm font-medium text-gray-700 mb-1">
                  Town*
                </label>
                <input
                  type="text"
                  id="town"
                  name="town"
                  value={formData.town}
                  onChange={handleChange}
                  className={`block w-full px-3 py-2 border ${errors.town ? 'border-red-500' : 'border-gray-300'} rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm`}
                />
                {errors.town && (
                  <p className="mt-1 text-sm text-red-600">{errors.town}</p>
                )}
              </div>
              
              {/* Province */}
              <div>
                <label htmlFor="province" className="block text-sm font-medium text-gray-700 mb-1">
                  Province*
                </label>
                <input
                  type="text"
                  id="province"
                  name="province"
                  value={formData.province}
                  onChange={handleChange}
                  className={`block w-full px-3 py-2 border ${errors.province ? 'border-red-500' : 'border-gray-300'} rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm`}
                />
                {errors.province && (
                  <p className="mt-1 text-sm text-red-600">{errors.province}</p>
                )}
              </div>
              
              {/* Postal Code */}
              <div>
                <label htmlFor="postalCode" className="block text-sm font-medium text-gray-700 mb-1">
                  Postal Code*
                </label>
                <input
                  type="text"
                  id="postalCode"
                  name="postalCode"
                  value={formData.postalCode}
                  onChange={handleChange}
                  className={`block w-full px-3 py-2 border ${errors.postalCode ? 'border-red-500' : 'border-gray-300'} rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm`}
                />
                {errors.postalCode && (
                  <p className="mt-1 text-sm text-red-600">{errors.postalCode}</p>
                )}
              </div>
            </div>
          </div>
          
          <div className="mb-6">
            <h2 className="text-lg font-medium text-gray-900 mb-4">Property Details</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Status */}
              <div>
                <label htmlFor="status" className="block text-sm font-medium text-gray-700 mb-1">
                  Status
                </label>
                <select
                  id="status"
                  name="status"
                  value={formData.status}
                  onChange={handleChange}
                  className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                >
                  <option value="Vacant Rental">Vacant</option>
                  <option value="Rented By Us">Occupied</option>
                  <option value="Rental Application In Progress">In Progress</option>
                </select>
              </div>
              
              {/* Landlord */}
              <div>
                <label htmlFor="landlordId" className="block text-sm font-medium text-gray-700 mb-1">
                  Landlord*
                </label>
                <select
                  id="landlordId"
                  name="landlordId"
                  value={formData.landlordId}
                  onChange={handleChange}
                  className={`block w-full px-3 py-2 border ${errors.landlordId ? 'border-red-500' : 'border-gray-300'} rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm`}
                  disabled={loadingLandlords}
                >
                  <option value="">Select Landlord</option>
                  {landlordOptions.map(landlord => (
                    <option key={landlord.id} value={landlord.id}>
                      {landlord.firstName} {landlord.lastName}
                    </option>
                  ))}
                </select>
                {errors.landlordId && (
                  <p className="mt-1 text-sm text-red-600">{errors.landlordId}</p>
                )}
                {loadingLandlords && (
                  <p className="mt-1 text-sm text-gray-500">Loading landlords...</p>
                )}
              </div>

              {/* Tenant */}
              <div>
                <label htmlFor="tenantId" className="block text-sm font-medium text-gray-700 mb-1">
                  Tenant (Optional)
                </label>
                <select
                  id="tenantId"
                  name="tenantId"
                  value={formData.tenantId}
                  onChange={handleChange}
                  className={`block w-full px-3 py-2 border ${errors.tenantId ? 'border-red-500' : 'border-gray-300'} rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm`}
                  disabled={loadingTenants}
                >
                  <option value="">Select Tenant</option>
                  {tenantOptions.map(tenant => (
                    <option key={tenant.id} value={tenant.id}>
                      {tenant.firstName} {tenant.lastName}
                    </option>
                  ))}
                </select>
                {errors.tenantId && (
                  <p className="mt-1 text-sm text-red-600">{errors.tenantId}</p>
                )}
                {loadingTenants && (
                  <p className="mt-1 text-sm text-gray-500">Loading tenants...</p>
                )}
              </div>
              
              {/* Agent */}
              <div>
                <label htmlFor="agentId" className="block text-sm font-medium text-gray-700 mb-1">
                  Agent (Optional)
                </label>
                <select
                  id="agentId"
                  name="agentId"
                  value={formData.agentId}
                  onChange={handleChange}
                  className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                  disabled={loadingAgents}
                >
                  <option value="">Select Agent</option>
                  {agentOptions.map(agent => (
                    <option key={agent.id} value={agent.id}>
                      {agent.firstName} {agent.lastName}
                    </option>
                  ))}
                </select>
                {loadingAgents && (
                  <p className="mt-1 text-sm text-gray-500">Loading agents...</p>
                )}
              </div>
              
              {/* Bedrooms */}
              <div>
                <label htmlFor="bedrooms" className="block text-sm font-medium text-gray-700 mb-1">
                  Bedrooms
                </label>
                <input
                  type="number"
                  id="bedrooms"
                  name="bedrooms"
                  value={formData.bedrooms}
                  onChange={handleChange}
                  className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                  min="0"
                  max="10"
                />
              </div>
              
              {/* Bathrooms */}
              <div>
                <label htmlFor="bathrooms" className="block text-sm font-medium text-gray-700 mb-1">
                  Bathrooms
                </label>
                <input
                  type="number"
                  id="bathrooms"
                  name="bathrooms"
                  value={formData.bathrooms}
                  onChange={handleChange}
                  className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                  min="0"
                  max="10"
                />
              </div>
              
              {/* Garages */}
              <div>
                <label htmlFor="garages" className="block text-sm font-medium text-gray-700 mb-1">
                  Garages
                </label>
                <input
                  type="number"
                  id="garages"
                  name="garages"
                  value={formData.garages}
                  onChange={handleChange}
                  className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                  min="0"
                  max="10"
                />
              </div>
              
              {/* Description */}
              <div className="md:col-span-2">
                <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-1">
                  Description
                </label>
                <textarea
                  id="description"
                  name="description"
                  value={formData.description}
                  onChange={handleChange}
                  rows="3"
                  className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                  placeholder="Additional details about the property..."
                />
              </div>
            </div>
          </div>
          
          <div className="mt-6 flex items-center justify-end space-x-3">
            <button
              type="button"
              onClick={() => navigate('/properties')}
              className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
              Cancel
            </button>
            <button
              type="submit"
              disabled={loadingCreate}
              className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              {loadingCreate ? (
                <>
                  <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Saving...
                </>
              ) : (
                <>
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  Save
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddProperty;